import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "@-/com.npmjs.react-native";
import { type SharedValue, useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useCallback } from "react";

export type HorizontalScrollEdges = {
  contentWidth: SharedValue<number>;
  onContentSizeChange: (width: number, height: number) => void;
  /** Measures the viewport: attach it to the scroller, or to a parent exactly as wide. */
  onLayout: (event: LayoutChangeEvent) => void;
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollX: SharedValue<number>;
  viewportWidth: SharedValue<number>;
};

/**
 * Tracks a horizontal scroller's offset and overflow for `HorizontalScrollEdgeFadeMask`. Every
 * measurement lives in a shared value read by the mask's UI-thread styles, so neither scrolling
 * nor a resize re-renders the owner.
 */
export function useHorizontalScrollEdges(): HorizontalScrollEdges {
  const scrollX = useSharedValue(0);
  const contentWidth = useSharedValue(0);
  const viewportWidth = useSharedValue(0);
  const onContentSizeChange = useCallback(
    (width: number) => contentWidth.set(width),
    [contentWidth],
  );
  const onLayout = useCallback(
    (event: LayoutChangeEvent) => viewportWidth.set(event.nativeEvent.layout.width),
    [viewportWidth],
  );
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      scrollX.set(contentOffset.x);
      // Every scroll event carries both sizes, so it also corrects a missed resize.
      contentWidth.set(contentSize.width);
      viewportWidth.set(layoutMeasurement.width);
    },
    [contentWidth, scrollX, viewportWidth],
  );
  return { contentWidth, onContentSizeChange, onLayout, onScroll, scrollX, viewportWidth };
}
